import React from 'react';
import styled, { keyframes, css } from 'styled-components';

export interface BarChartData {
  label: string;
  value: number;
}

interface BarChartProps {
  data: BarChartData[];
  multiple?: number;
  percent?: boolean;
  barHeight?: string;
  barPadding?: string;
  fontSize?: string;
}

const TeamPillarBarChart: React.FC<BarChartProps> = ({
  data,
  multiple = 1,
  percent = true,
  barHeight = '16px',
  barPadding = '1px',
  fontSize = '11px',
}) => (
  <BarContainer>
    {data?.map((row, index) => (
      <BarRow key={index} barPadding={barPadding}>
        <BarTitle fontSize={fontSize}>{row.label}</BarTitle>
        <Bar barHeight={barHeight}>
          <BarFill percentage={row.value * multiple}>
            <span>
              {row.value}
              {percent ? '%' : ''}
            </span>
          </BarFill>
        </Bar>
      </BarRow>
    ))}
  </BarContainer>
);
export default TeamPillarBarChart;

const growAnimation = (percentage: number) => keyframes`
  from {
    width: 0%;
  }
  to {
    width: ${percentage}%;
  }
`;

const BarContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  gap: 3px;
  padding-left: 0px;
`;

const BarRow = styled.div<{ barPadding: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${props => props.barPadding}};
`;

const BarTitle = styled.div<{ fontSize: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => props.fontSize}};
  color: #333;
  font-weight: 400;
  flex: 2; 
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
`;

const Bar = styled.div<{ barHeight: string }>`
  position: relative;
  height: ${props => props.barHeight}};
  background-color: #ddd;
  border-radius: 4px;
  overflow: visible;
  flex: 3; 
  cursor: pointer;
  transition: box-shadow 0.1s ease-in-out;

  &:hover {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
  }

  &:hover span {
    visibility: visible;
    opacity: 1;
  }
`;

const BarFill = styled.div<{ percentage: number }>`
  height: 100%;
  border-radius: 4px;
  background-color: ${props =>
    props.percentage < 40
      ? 'rgba(255, 50, 90, .4)'
      : props.percentage <= 60
        ? 'rgba(255, 216, 111, 1)'
        : 'rgba(129, 199, 132, .7)'};
  animation: ${props => css`
    ${growAnimation(props.percentage)} 0.8s ease-out
  `};
  animation-fill-mode: forwards;
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  span {
    visibility: hidden;
    opacity: 0;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: bold;
    transition: opacity 0.1s ease-in-out;
  }
`;
